import { useCallback, useEffect, useState } from 'react';
import {
  agentStatusLabel,
  agentStatusTone,
  fetchCloudTalkLogs,
  formatCloudTalkLogTime,
} from '../../lib/demCloudTalk';
import type { DemCloudTalkLog } from '../../types/dem';

function StatusCell({ status, emphasize = false }: { status: string; emphasize?: boolean }) {
  const tone = agentStatusTone(status);
  return (
    <span
      className={`dem-cloudtalk-log-status dem-cloudtalk-log-status--${tone}${
        emphasize ? ' dem-cloudtalk-log-status--new' : ''
      }`}
    >
      {agentStatusLabel(status)}
    </span>
  );
}

/** Full CloudTalk agent status history for Settings → Activity log. */
export function CloudTalkAgentLogsPanel() {
  const [logs, setLogs] = useState<DemCloudTalkLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLogs(await fetchCloudTalkLogs(500));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load CloudTalk agent logs.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2 className="settings-panel__title">CloudTalk agent logs</h2>
          <p className="page-lead" style={{ margin: '0.35rem 0 0' }}>
            Agent online / offline / on-call transitions from CloudTalk (SGT).
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <p className="alert alert-error">{error}</p>}

      {loading ? (
        <p className="dem-cloudtalk-empty">Loading…</p>
      ) : logs.length === 0 ? (
        <p className="dem-cloudtalk-empty">No CloudTalk agent logs yet.</p>
      ) : (
        <div className="dem-cloudtalk-logs-table">
          <div className="dem-cloudtalk-logs-table__head" aria-hidden>
            <span>Timestamp (SGT)</span>
            <span>Agent</span>
            <span>Previous</span>
            <span>New status</span>
          </div>
          {logs.map((log) => (
            <div key={log.log_id || `${log.time}|${log.agent}`} className="dem-cloudtalk-logs-row">
              <span className="dem-cloudtalk-logs-row__time">{formatCloudTalkLogTime(log.time)}</span>
              <span className="dem-cloudtalk-logs-row__agent">{log.agent}</span>
              <StatusCell status={log.old_status} />
              <StatusCell status={log.new_status} emphasize />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
